import { Link } from 'react-router-dom';
import type { JSX } from 'react';
import { UnitShowingTriggerButton } from '@/routes/publicProperties/components/UnitEngagementDialog';
import type { PublishedUnitSummary, PublicPropertySummary } from '@/lib/types/publicProperty';
import type { AvailabilitiesQuickViewContent } from '@/lib/types/siteSettings';
import {
  formatPropertyAddress,
  formatSquareFeet,
  formatUnitLabel,
} from '@/lib/utils/propertyFormatting';
import { formatMonthlyLeaseAmount } from '@/routes/publicProperties/rentFormatting';
import { formatUnitAvailabilityLabel, unitHasIncentives } from './availabilitiesMapListHelpers';
import { applyTemplate } from '@/lib/utils/textTemplates';

export function AvailabilitiesUnitQuickView({
  property,
  unit,
  content,
  availabilityLabels,
}: {
  property: PublicPropertySummary;
  unit: PublishedUnitSummary;
  content: AvailabilitiesQuickViewContent;
  availabilityLabels: { immediateLabel: string; negotiableLabel: string };
}): JSX.Element {
  const addressLabel = formatPropertyAddress(property.address);
  const areaLabel = formatSquareFeet(unit.areaSF);
  const rentLabel = formatMonthlyLeaseAmount(unit);
  const availabilityLabel = formatUnitAvailabilityLabel(unit, availabilityLabels);
  const unitDisplay = formatUnitLabel(unit);
  const unitHref = `/properties/${property.propertyCode.toLowerCase()}/units/${unit.id}`;
  const titleLabel = applyTemplate(content.titleTemplate, {
    unitLabel: unitDisplay,
    unitCode: unit.unitCode,
    level: unit.floorLevel,
    floor: unit.floorLevel,
    floorLevel: unit.floorLevel,
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-accent">
          {content.eyebrow}
        </p>
        <h4 className="text-lg font-semibold text-brand-primary">{titleLabel}</h4>
        <p className="text-sm text-text-primary/70">{addressLabel}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {unit.potentiallyDivisible ? (
          <span className="inline-flex items-center rounded-full bg-brand-primary/10 px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-wide text-brand-primary">
            {content.divisibleLabel}
          </span>
        ) : null}
        {unitHasIncentives(unit) ? (
          <span className="inline-flex items-center rounded-full bg-brand-highlight/35 px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-wide text-brand-primary">
            {content.incentivesLabel}
          </span>
        ) : null}
      </div>

      <div className="grid gap-3 rounded-2xl border border-surface-subtle bg-white p-4">
        <div className="flex items-baseline justify-between gap-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
            {content.rentLabel}
          </p>
          <p className="text-sm font-semibold text-brand-primary">
            {rentLabel} {content.rentSuffix}
          </p>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
            {content.sizeLabel}
          </p>
          <p className="text-sm font-semibold text-brand-primary">{areaLabel} sq ft</p>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
            {content.availabilityLabel}
          </p>
          <p className="text-sm font-semibold text-brand-primary">{availabilityLabel}</p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <UnitShowingTriggerButton
          summary={property}
          unit={unit}
          label={content.scheduleLabel}
          className="min-h-11 w-full justify-center"
        />
        <Link
          to={unitHref}
          className="inline-flex min-h-11 items-center justify-center rounded-xl border border-brand-primary/30 bg-white px-4 py-3 text-sm font-semibold text-brand-primary shadow-sm transition hover:border-brand-primary hover:bg-brand-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent"
        >
          {content.detailsLabel}
        </Link>
      </div>
    </div>
  );
}
